import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import {
  assignSubCategoryToCategory,
  createSubCategoryQuick,
  fetchActiveSubCategories,
  fetchSubcategoriesByCategory,
} from "../../../services/subcategories";
import {
  hiddenChoiceItemsFromKeep,
  syncProductChoicesAndItems,
} from "../../../services/MenuService";
import {
  productChoiceChildren,
  productTitle,
  subcategoryTitle,
} from "../Cms/MenuTree/menuTreeUtils";
import ProductChoiceKeepPanel from "../Cms/MenuTree/ProductChoiceKeepPanel";
import { IMAGE_PICKER_ACCEPT, validateImageFile } from "../../../utils/imageValidation";
import "../../../i18n";
import "../Cms/MenuTree/MenuTree.css";
import "./AddCategory.css";

const REQUIRED_LANG = "en";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
});

const CategoryEditing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const languages = useMemo(() => getMenuLanguages(t), [t]);
  const englishLang = languages.find((l) => l.code === REQUIRED_LANG) || languages[0];
  const otherLangs = languages.filter((l) => l.code !== REQUIRED_LANG);

  const [loading, setLoading] = useState(true);
  const [displayInCategoryList, setDisplayInCategoryList] = useState(false);
  const [displayOrientation, setDisplayOrientation] = useState("vertical");
  const [savedPhoto, setSavedPhoto] = useState(null);

  const [titles, setTitles] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, ""]))
  );
  const [descriptions, setDescriptions] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, ""]))
  );
  const [openLangs, setOpenLangs] = useState({});
  const setTitle = (code, value) => setTitles((prev) => ({ ...prev, [code]: value }));
  const hasAnyTitle = Object.values(titles).some((v) => v?.trim());
  const isOpen = (code) => Boolean(openLangs[code]) || Boolean(titles[code]?.trim());

  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const previewSrc =
    imageUrl ||
    (!photoRemoved && savedPhoto ? `${config.UPLOADS_SERVER}/category/${savedPhoto}` : null);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    const { isValid, message } = validateImageFile(selected, 5);
    if (!isValid) {
      toast.error(message);
      return;
    }
    setFile(selected);
    setImageUrl(URL.createObjectURL(selected));
    setPhotoRemoved(false);
  };

  const [allSubs, setAllSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);
  const [selectedSubIds, setSelectedSubIds] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [newSubName, setNewSubName] = useState("");
  const [creatingSub, setCreatingSub] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [kept, setKept] = useState({});
  const [openPanels, setOpenPanels] = useState({});
  const [subsOpen, setSubsOpen] = useState(false);
  const [keptChoices, setKeptChoices] = useState({});
  const [keptItems, setKeptItems] = useState({});
  const [openProductChoices, setOpenProductChoices] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [categoryRes, assigned, all] = await Promise.all([
          axios.get(`${config.dev}/v2/categories/retrieve/${id}`),
          fetchSubcategoriesByCategory(id),
          fetchActiveSubCategories(),
        ]);
        if (cancelled) return;

        const category = categoryRes.data || {};
        const nextTitles = {};
        const nextDescriptions = {};
        const nextOpen = {};
        MENU_LANGUAGE_CODES.forEach((code) => {
          const langData = category.categoryDetails?.find((d) => d.language === code);
          nextTitles[code] = langData?.categoryTitle || "";
          nextDescriptions[code] = langData?.categoryDescription || "";
          if (code !== REQUIRED_LANG && nextTitles[code].trim()) nextOpen[code] = true;
        });
        setTitles(nextTitles);
        setDescriptions(nextDescriptions);
        setOpenLangs(nextOpen);
        setDisplayInCategoryList(!!category.displayInCategoryList);
        setDisplayOrientation(category.displayOrientation || "vertical");
        setSavedPhoto(category.photo || null);
        setPhotoRemoved(false);
        setFile(null);
        setImageUrl(null);

        const assignedList = Array.isArray(assigned) ? assigned : [];
        const assignedIds = assignedList.map((s) => String(s._id)).filter(Boolean);
        const catalog = Array.isArray(all) ? all : [];
        const merged = [...catalog];
        assignedList.forEach((sub) => {
          if (!merged.some((s) => String(s._id) === String(sub._id))) merged.unshift(sub);
        });
        setAllSubs(merged);
        setSelectedSubIds(assignedIds);
        setOpenPanels(Object.fromEntries(assignedIds.map((subId) => [subId, true])));
        setSubsOpen(assignedIds.length > 0);
      } catch (err) {
        console.error("CategoryEditing: load failed", err);
        toast.error(t("errorTitle"));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSubsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const subsById = useMemo(
    () => Object.fromEntries(allSubs.map((s) => [String(s._id), s])),
    [allSubs]
  );
  const query = subSearch.trim().toLowerCase();
  const visibleSubs = allSubs.filter(
    (s) => !query || subcategoryTitle(s, "").toLowerCase().includes(query)
  );

  const productsOf = (sub) =>
    (sub?.productList || [])
      .filter((p) => p?.product != null && p.product.isArchived !== true)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((p) => ({
        id: String(p.product._id || p.product),
        label: productTitle(p.product),
        product: p.product,
      }))
      .filter((p) => p.id && p.id !== "undefined");

  const keptIdsFor = (subId, products) => {
    const all = products.map((p) => p.id);
    const custom = kept[subId];
    return Array.isArray(custom) ? all.filter((pid) => custom.includes(pid)) : all;
  };

  const toggleSub = (subId) => {
    const wasSelected = selectedSubIds.includes(subId);
    setSelectedSubIds((prev) => (wasSelected ? prev.filter((x) => x !== subId) : [...prev, subId]));
    if (!wasSelected) setOpenPanels((prev) => ({ ...prev, [subId]: true }));
  };

  const setKeptFor = (subId, ids) => setKept((prev) => ({ ...prev, [subId]: ids }));
  const togglePanel = (subId, fallback) =>
    setOpenPanels((prev) => ({ ...prev, [subId]: !(prev[subId] ?? fallback) }));

  const cancelCreateSub = () => {
    if (creatingSub) return;
    setCreatingNew(false);
    setNewSubName("");
  };

  const handleCreateSub = async () => {
    const name = newSubName.trim();
    if (!name || creatingSub) return;
    setCreatingSub(true);
    try {
      const created = await createSubCategoryQuick(name);
      if (!created?._id) throw new Error("no id");
      const subId = String(created._id);
      setAllSubs((prev) => (prev.some((s) => String(s._id) === subId) ? prev : [created, ...prev]));
      setSelectedSubIds((prev) => (prev.includes(subId) ? prev : [...prev, subId]));
      setOpenPanels((prev) => ({ ...prev, [subId]: true }));
      setNewSubName("");
      setSubSearch("");
      setCreatingNew(false);
      setSubsOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || t("catFormSubcategoryCreateFailed"));
    } finally {
      setCreatingSub(false);
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const canSubmit = hasAnyTitle && !submitting && !loading;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    const categoryDetails = languages
      .map((l) => ({
        language: l.code,
        categoryTitle: titles[l.code]?.trim() || "",
        categoryDescription: descriptions[l.code]?.trim() || "",
      }))
      .filter((d) => d.categoryTitle);

    if (!categoryDetails.some((d) => d.categoryTitle)) {
      swal(t("warningTitle"), t("catFormTitleRequired"), "warning");
      return;
    }

    setSubmitting(true);
    try {
      if (file) {
        const photoData = new FormData();
        photoData.append("file", file);
        const photoRes = await axios.put(`${config.dev}/v2/categories/photos/${id}`, photoData, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        if (photoRes.data?.photo) setSavedPhoto(photoRes.data.photo);
        setFile(null);
        setImageUrl(null);
        setPhotoRemoved(false);
      }

      const payload = {
        categoryDetails,
        displayInCategoryList,
        displayOrientation,
      };
      if (photoRemoved) payload.photo = null;
      await axios.put(`${config.dev}/v2/categories/edit/${id}`, payload, {
        headers: authHeaders(),
      });

      try {
        await assignSubCategoryToCategory({
          categoryId: String(id),
          subcategories: selectedSubIds.map((subCategoryId, index) => ({
            subCategoryId,
            order: index + 1,
          })),
        });
        const synced = new Set();
        for (const subId of selectedSubIds) {
          const products = productsOf(subsById[subId]);
          const keptProductIds = keptIdsFor(subId, products);
          for (const product of products) {
            if (!keptProductIds.includes(product.id) || synced.has(product.id)) continue;
            const children = productChoiceChildren(product.product);
            if (!children.length) continue;
            synced.add(product.id);
            const keptChoiceIds = keptChoices[product.id] ?? children.map((c) => c.id);
            await syncProductChoicesAndItems(
              product.id,
              keptChoiceIds,
              hiddenChoiceItemsFromKeep(children, keptChoiceIds, keptItems[product.id] || {})
            );
          }
        }
      } catch (err) {
        console.error("CategoryEditing: assign subcategories failed", err);
        toast.warn(t("catFormAttachFailed"));
      }

      swal({
        title: t("successTitle"),
        text: t("categoryUpdated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      });
      setTimeout(() => navigate(`/category-details/${id}`), 1000);
    } catch (err) {
      console.error("CategoryEditing: save failed", err?.response?.data || err.message);
      swal(t("errorTitle"), err?.response?.data?.message || String(err), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const langInput = (lang, required = false) => (
    <div className="catform__field" key={lang.code}>
      <label className="catform__label" htmlFor={`edit-cat-title-${lang.code}`}>
        <img src={lang.flag} alt="" />
        {lang.label}
        {required && <span className="catform__required">{t("catFormRequired")}</span>}
      </label>
      <input
        id={`edit-cat-title-${lang.code}`}
        type="text"
        className="catform__input"
        value={titles[lang.code] || ""}
        onChange={(e) => setTitle(lang.code, e.target.value)}
      />
    </div>
  );

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("editCat")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("editCat")} motherMenu={t("kitchen")} />

      <form className="catform" onSubmit={handleSubmit} noValidate>
        <div className="catform__top">
          <h1 className="catform__title">{t("editCat")}</h1>
          <button
            type="button"
            className="btn catform__link"
            onClick={() => navigate(`/category-details/${id}`)}
          >
            <i className="fas fa-arrow-left" aria-hidden /> {t("viewCategoryDetails")}
          </button>
        </div>

        <section className="catform__section">
          <h2 className="catform__heading">
            {t("catFormPhoto")} <span className="catform__optional">{t("optional")}</span>
          </h2>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept={IMAGE_PICKER_ACCEPT}
            onChange={handleFileChange}
          />
          <div className="catform__photo">
            <button type="button" className="catform__thumb" onClick={() => fileInputRef.current?.click()}>
              {previewSrc ? <img src={previewSrc} alt="" /> : <i className="fas fa-camera" aria-hidden />}
            </button>
            <div className="catform__photo-actions">
              <button type="button" className="btn catform__small" onClick={() => fileInputRef.current?.click()}>
                {previewSrc ? t("catFormReplacePhoto") : t("catFormChoosePhoto")}
              </button>
              {previewSrc && (
                <button
                  type="button"
                  className="btn catform__small is-danger"
                  onClick={() => {
                    setFile(null);
                    setImageUrl(null);
                    setPhotoRemoved(true);
                  }}
                >
                  {t("catFormRemovePhoto")}
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">{t("catFormStepName")}</h2>
          {langInput(englishLang)}
          <p className="catform__pills-label">{t("catFormOtherLanguages")}</p>
          <div className="catform__pills">
            {otherLangs.map((lang) => {
              const filled = Boolean(titles[lang.code]?.trim());
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`catform__pill ${isOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                  aria-pressed={isOpen(lang.code)}
                  onClick={() => setOpenLangs((prev) => ({ ...prev, [lang.code]: !prev[lang.code] }))}
                >
                  <img src={lang.flag} alt="" />
                  {lang.label}
                  {filled && <i className="fas fa-check" aria-hidden />}
                </button>
              );
            })}
          </div>
          {otherLangs.filter((lang) => isOpen(lang.code)).map((lang) => langInput(lang))}
        </section>

        <section className="catform__section catform__section--orient">
          <div className="catform__row">
            <h2 className="catform__heading">{t("displayOrientation")}</h2>
            <div className="catform__radios">
              {["horizontal", "vertical"].map((value) => (
                <label
                  key={value}
                  className={`catform__radio ${displayOrientation === value ? "is-on" : ""}`}
                >
                  <input
                    type="radio"
                    name="categoryDisplayOrientation"
                    value={value}
                    checked={displayOrientation === value}
                    onChange={() => setDisplayOrientation(value)}
                  />
                  {t(value)}
                </label>
              ))}
            </div>
          </div>
        </section>

        <section className={`catform__section ${subsOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="catform__fold"
            aria-expanded={subsOpen}
            aria-controls="edit-catform-subs"
            onClick={() => setSubsOpen((open) => !open)}
          >
            <h2 className="catform__heading">
              {t("catFormStepSubcategories")} <span className="catform__optional">{t("optional")}</span>
              {selectedSubIds.length > 0 && (
                <span className="catform__fold-count">{selectedSubIds.length}</span>
              )}
            </h2>
            <i
              className={`fas fa-chevron-down catform__fold-arrow ${subsOpen ? "is-open" : ""}`}
              aria-hidden
            />
          </button>
          <p className="catform__help">{t("catFormSubcategoriesHelp")}</p>

          {subsOpen && (
            <div id="edit-catform-subs" className="catform__fold-body">
              {selectedSubIds.length > 0 && (
                <div className="catform__chips">
                  {selectedSubIds.map((subId) => (
                    <span key={subId} className="catform__chip">
                      {subcategoryTitle(subsById[subId], t("catFormUnknownSubcategory"))}
                      <button type="button" aria-label={t("remove")} onClick={() => toggleSub(subId)}>
                        <i className="fas fa-times" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                type="search"
                className="catform__input"
                placeholder={t("catFormSearchSubcategories")}
                value={subSearch}
                onChange={(e) => setSubSearch(e.target.value)}
              />

              <div className="catform__list">
                {subsLoading ? (
                  <div className="catform__empty">{t("loading")}…</div>
                ) : allSubs.length === 0 ? (
                  <div className="catform__empty">{t("catFormNoSubcategories")}</div>
                ) : visibleSubs.length === 0 ? (
                  <div className="catform__empty">{t("catFormNoMatch")}</div>
                ) : (
                  visibleSubs.map((sub) => {
                    const subId = String(sub._id);
                    const checked = selectedSubIds.includes(subId);
                    const products = productsOf(sub);
                    const total = products.length;
                    const keptIds = keptIdsFor(subId, products);
                    const keptCount = keptIds.length;
                    const hasPanel = checked && total > 0;
                    const panelOpen = hasPanel && (openPanels[subId] ?? true);
                    const panelId = `edit-catform-panel-${subId}`;
                    return (
                      <div
                        key={subId}
                        className={`catform__item ${checked ? "is-checked" : ""} ${
                          panelOpen ? "is-expanded" : ""
                        }`}
                      >
                        <div className="catform__item-head">
                          <label className={`catform__option ${checked ? "is-checked" : ""}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleSub(subId)} />
                            <span className="catform__option-name">{subcategoryTitle(sub)}</span>
                            {total > 0 && (
                              <span className={`menu-picker__badge ${hasPanel ? "menu-picker__badge--kept" : ""}`}>
                                {hasPanel
                                  ? t("menuTreeKeptBadge", { kept: keptCount, total })
                                  : t("menuTreeProductsBadge", { count: total })}
                              </span>
                            )}
                          </label>
                          {hasPanel && (
                            <button
                              type="button"
                              className={`btn menu-picker__chevron ${panelOpen ? "is-open" : ""}`}
                              onClick={() => togglePanel(subId, true)}
                              aria-expanded={panelOpen}
                              aria-controls={panelId}
                              aria-label={panelOpen ? t("menuTreeCollapse") : t("menuTreeExpand")}
                            >
                              <i className="fas fa-chevron-down" aria-hidden />
                            </button>
                          )}
                        </div>

                        {panelOpen && (
                          <div className="menu-picker__panel" id={panelId}>
                            <div className="menu-picker__panel-head">
                              <span className="menu-picker__panel-summary">
                                <strong>{keptCount}</strong> {t("menuTreeKeptSummary", { total })}
                              </span>
                              <button
                                type="button"
                                className="btn menu-picker__panel-toggle"
                                onClick={() =>
                                  setKeptFor(subId, keptCount === total ? [] : products.map((p) => p.id))
                                }
                              >
                                {keptCount === total ? t("menuTreeUncheckAll") : t("menuTreeCheckAll")}
                              </button>
                            </div>
                            <div
                              className="menu-picker__progress"
                              role="progressbar"
                              aria-valuemin={0}
                              aria-valuemax={total}
                              aria-valuenow={keptCount}
                            >
                              <span style={{ width: `${total ? (keptCount / total) * 100 : 0}%` }} />
                            </div>
                            <div className="menu-picker__products">
                              {products.map((product) => {
                                const on = keptIds.includes(product.id);
                                const switchId = `edit-catform-${subId}-${product.id}`;
                                const initial = String(product.label || "?").trim().charAt(0).toUpperCase();
                                const choices = productChoiceChildren(product.product);
                                const choiceKey = `${subId}:${product.id}`;
                                const choicePanelOpen =
                                  on && choices.length > 0 && (openProductChoices[choiceKey] ?? true);
                                return (
                                  <div key={product.id}>
                                    <div className="menu-picker__head">
                                      <label
                                        htmlFor={switchId}
                                        className={`menu-picker__product ${on ? "is-on" : ""}`}
                                      >
                                        <span className="menu-picker__avatar" aria-hidden>
                                          {initial || "?"}
                                        </span>
                                        <span className="menu-picker__product-label">{product.label}</span>
                                        {choices.length > 0 && (
                                          <span
                                            className={`menu-picker__badge ${
                                              on ? "menu-picker__badge--kept" : ""
                                            }`}
                                          >
                                            {on
                                              ? t("menuTreeKeptBadge", {
                                                  kept: (keptChoices[product.id] ?? choices.map((c) => c.id))
                                                    .length,
                                                  total: choices.length,
                                                })
                                              : t("menuTreeChoicesBadge", { count: choices.length })}
                                          </span>
                                        )}
                                        <input
                                          id={switchId}
                                          type="checkbox"
                                          className="menu-picker__switch"
                                          role="switch"
                                          checked={on}
                                          aria-checked={on}
                                          onChange={() =>
                                            setKeptFor(
                                              subId,
                                              on
                                                ? keptIds.filter((pid) => pid !== product.id)
                                                : [...keptIds, product.id]
                                            )
                                          }
                                        />
                                      </label>
                                      {on && choices.length > 0 && (
                                        <button
                                          type="button"
                                          className={`btn menu-picker__chevron ${
                                            choicePanelOpen ? "is-open" : ""
                                          }`}
                                          onClick={() =>
                                            setOpenProductChoices((prev) => ({
                                              ...prev,
                                              [choiceKey]: !(prev[choiceKey] ?? true),
                                            }))
                                          }
                                          aria-expanded={choicePanelOpen}
                                        >
                                          <i className="fas fa-chevron-down" aria-hidden />
                                        </button>
                                      )}
                                    </div>
                                    {choicePanelOpen && (
                                      <div className="menu-picker__child-panel">
                                        <ProductChoiceKeepPanel
                                          productId={product.id}
                                          choices={choices}
                                          keptChoiceIds={keptChoices[product.id]}
                                          onKeptChoicesChange={(ids) =>
                                            setKeptChoices((prev) => ({ ...prev, [product.id]: ids }))
                                          }
                                          keptItems={keptItems[product.id] || {}}
                                          onKeptItemsChange={(choiceId, ids) =>
                                            setKeptItems((prev) => ({
                                              ...prev,
                                              [product.id]: {
                                                ...(prev[product.id] || {}),
                                                [choiceId]: ids,
                                              },
                                            }))
                                          }
                                          t={t}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="catform__new">
                {creatingNew ? (
                  <div className="menu-picker__create-form">
                    <input
                      type="text"
                      className="menu-picker__create-input"
                      placeholder={t("catFormNewSubcategoryPlaceholder")}
                      aria-label={t("catFormNewSubcategoryPlaceholder")}
                      value={newSubName}
                      disabled={creatingSub}
                      autoFocus
                      onChange={(e) => setNewSubName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateSub();
                        }
                        if (e.key === "Escape") cancelCreateSub();
                      }}
                    />
                    <button
                      type="button"
                      className="btn menu-picker__create-submit"
                      disabled={!newSubName.trim() || creatingSub}
                      onClick={handleCreateSub}
                    >
                      {creatingSub ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                      ) : (
                        t("menuTreeCreate")
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn menu-picker__create-cancel"
                      onClick={cancelCreateSub}
                      disabled={creatingSub}
                      aria-label={t("cancel")}
                    >
                      <i className="fas fa-times" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn menu-picker__create" onClick={() => setCreatingNew(true)}>
                    <span className="menu-picker__create-plus" aria-hidden>
                      <i className="fas fa-plus" />
                    </span>
                    {t("menuTreeNewSubcategory")}
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate(`/category-details/${id}`)}
            disabled={submitting}
          >
            {t("cancel")}
          </button>
          <button type="submit" className="btn catform__btn catform__btn--primary" disabled={!canSubmit}>
            {submitting ? t("loading") : t("saveChanges")}
          </button>
        </div>
      </form>
    </>
  );
};

export default CategoryEditing;
